import { Injectable } from '@angular/core';
import { Amenity, IAmenity } from '../models/amenity.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { AwsService } from './aws.service';
import { IPresignResponse } from '../models/presign-response.model';

@Injectable({
  providedIn: 'root'
})
export class AmenityService {

  public loading: boolean = false;
  public amenities: Amenity[] = [];

  constructor(
    public authService: AuthService,
    public awsService: AwsService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create(
    neighborhoodId: string,
    amenity: IAmenity,
    file?: File,
    regulationFile?: File
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/amenities`;
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando amenidad';

    try {
      const token = await this.authService.getToken();

      const body: any = { ...amenity };
      if (file) {
        body.contentType = file.type;
      }
      if (regulationFile) {
        body.regulationContentType = regulationFile.type;
        body.regulationSize = regulationFile.size;
      }

      const response = await new Promise<ServerResponse<{ presignFile: IPresignResponse, presignRegulation: IPresignResponse }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ presignFile: IPresignResponse, presignRegulation: IPresignResponse }>>(
            urlApi,
            JSON.stringify(body),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{ presignFile: IPresignResponse, presignRegulation: IPresignResponse }>
            ) => resolve(new ServerResponse<{ presignFile: IPresignResponse, presignRegulation: IPresignResponse }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );

      if (response.statusCode === 201) {
        flag = true;

        /* Imagen y reglamento se suben uno tras otro: el spinner dice
           "Archivo 1 de 2" cuando son los dos. */
        const total = (
          (file && response.data?.presignFile?.uploadUrl ? 1 : 0) +
          (regulationFile && response.data?.presignRegulation?.uploadUrl ? 1 : 0)
        );
        let index = 0;

        if (file && response.data?.presignFile?.uploadUrl) {
          await this.awsService.uploadFile(
            response.data.presignFile.uploadUrl,
            file,
            file.type,
            file.name,
            total > 1 ? { index: ++index, total } : null
          );
        }

        if (regulationFile && response.data?.presignRegulation?.uploadUrl) {
          await this.awsService.uploadFile(
            response.data.presignRegulation.uploadUrl,
            regulationFile,
            regulationFile.type,
            regulationFile.name,
            total > 1 ? { index: ++index, total } : null
          );
        }

        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll(neighborhoodId);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    amenity: Amenity
  ): Promise<boolean> {
  
    let flag = false;
  
    const urlApi = `${environment.urlApi}/neighborhoods/${amenity.neighborhoodId}/amenities/${amenity.amenityId}`;
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando amenidad';
  
    try {
      const token = await this.authService.getToken();
  
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            {
              headers: {
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse
            ) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );
  
      if (response.statusCode === 200) {
        flag = true;
        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll(amenity.neighborhoodId);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
  
    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }
  
    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;
  }
  
  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  async get(
    neighborhoodId: string,
    amenityId: string
  ): Promise<Amenity | undefined> {
  
    let amenity: Amenity | undefined = undefined;
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/amenities/${amenityId}`;
  
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Obteniendo amenidad';
  
    try {
      const token = await this.authService.getToken();
  
      const response = await new Promise<ServerResponse<{ amenity: IAmenity }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ amenity: IAmenity }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{ amenity: IAmenity }>
            ) => resolve(new ServerResponse<{ amenity: IAmenity }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );
  
      if (response.statusCode === 200) {
        amenity = new Amenity(response.data!.amenity);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
  
    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }
  
    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return amenity;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  async getAll(
    neighborhoodId: string
  ) {
    this.loading = true;
  
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/amenities`;
  
    try {
      const token = await this.authService.getToken();
  
      const response = await new Promise<ServerResponse<{ amenities: IAmenity[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ amenities: IAmenity[] }>>(
            urlApi,
            {
              headers: {
                'content-type': 'application/json',
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{ amenities: IAmenity[] }>
            ) => resolve(new ServerResponse<{ amenities: IAmenity[] }>(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );

      console.log(response);
      
  
      if (response.statusCode === 200) {
        this.amenities = response.data!.amenities.map(
          amenity => new Amenity(amenity)
        );
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }
  
    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }
  
    this.loading = false;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(
    amenity: Amenity,
    file?: File,
    regulationFile?: File
  ): Promise<boolean> {

    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${amenity.neighborhoodId}/amenities/${amenity.amenityId}`;
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando amenidad';

    try {
      const token = await this.authService.getToken();

      const body: any = { ...amenity };
      if (file) {
        body.contentType = file.type;
      }
      if (regulationFile) {
        body.regulationContentType = regulationFile.type;
        body.regulationSize = regulationFile.size;
      }

      const response = await new Promise<ServerResponse<{ presignFile: IPresignResponse, presignRegulation: IPresignResponse }>>(
        (resolve, reject) => {
          this.http.put<IServerResponse<{ presignFile: IPresignResponse, presignRegulation: IPresignResponse }>>(
            urlApi,
            JSON.stringify(body),
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (
              response: IServerResponse<{ presignFile: IPresignResponse, presignRegulation: IPresignResponse }>
            ) => resolve(
              new ServerResponse<{ presignFile: IPresignResponse, presignRegulation: IPresignResponse }>(response)
            ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                this.toastService.show({
                  header: 'Error de servidor',
                  body: `${error}`,
                  classname: 'bg-danger text-white'
                });
                reject(error);
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {
        flag = true;

        /* Imagen y reglamento se suben uno tras otro: el spinner dice
           "Archivo 1 de 2" cuando son los dos. */
        const total = (
          (file && response.data?.presignFile?.uploadUrl ? 1 : 0) +
          (regulationFile && response.data?.presignRegulation?.uploadUrl ? 1 : 0)
        );
        let index = 0;

        if (file && response.data?.presignFile?.uploadUrl) {
          await this.awsService.uploadFile(
            response.data.presignFile.uploadUrl,
            file,
            file.type,
            file.name,
            total > 1 ? { index: ++index, total } : null
          );
        }

        if (regulationFile && response.data?.presignRegulation?.uploadUrl) {
          await this.awsService.uploadFile(
            response.data.presignRegulation.uploadUrl,
            regulationFile,
            regulationFile.type,
            regulationFile.name,
            total > 1 ? { index: ++index, total } : null
          );
        }

        this.toastService.show({
          body: `${response.message}`,
          classname: 'bg-success text-white'
        });
        this.getAll(amenity.neighborhoodId);
      } else {
        this.toastService.show({
          header: 'Error de servidor',
          body: `${response.message}`,
          classname: 'bg-danger text-white'
        });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({
        header: 'Error',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

}
